import React from "react"
import styled from "styled-components"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Home, ChevronRight, Bookmark, Package, Users, Clock, Bell,
  Settings, HelpCircle, Crown, LogOut, Box, Plus, Bug
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

const Sidebar = styled.aside`
  position: fixed;
  top: 0; left: 0; bottom: 0;
  width: 240px;
  background: ${p => p.theme.cardBackground};
  border-right: 1px solid ${p => p.theme.border};
  display: flex;
  flex-direction: column;
  padding: 22px 14px 16px;
  overflow-y: auto;
  z-index: 100;

  @media (max-width: 768px) { width: 200px; padding: 16px 10px 10px; }
  @media (max-width: 650px) { width: 0; padding: 0; overflow: hidden; }
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px 20px;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-family: 'Russo One', 'Bungee', 'Bebas Neue', 'Pretendard', system-ui, sans-serif;

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
    color: #ffffff;
    font-size: 13px;
    letter-spacing: -0.5px;
    box-shadow: 0 6px 14px rgba(124,58,237,0.35);
    transform: rotate(-6deg);
    flex-shrink: 0;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 9px;
      border: 1.5px solid rgba(255,255,255,0.30);
      pointer-events: none;
    }
  }

  .text {
    display: inline-flex;
    flex-direction: column;
    line-height: 1;
    gap: 2px;
  }

  .title {
    font-size: 18px;
    color: ${p => p.theme.textPrimary};
    letter-spacing: 0.5px;
    line-height: 1;
  }

  .accent {
    color: transparent;
    background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .sub {
    font-family: 'Bebas Neue', 'Pretendard', sans-serif;
    font-size: 9px;
    letter-spacing: 4px;
    color: ${p => p.theme.textMuted};
    line-height: 1;
  }
`

const NavLink = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 11px;
  background: ${p => p.$active ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.10))' : 'transparent'};
  border: none;
  border-radius: 10px;
  color: ${p => p.$active ? '#7c3aed' : p.theme.textSecondary};
  font-size: 13px;
  font-weight: ${p => p.$active ? 700 : 600};
  cursor: pointer;
  transition: all 0.12s;
  text-align: left;
  margin: 1px 0;

  &:hover {
    background: ${p => p.$active ? 'linear-gradient(135deg, rgba(124,58,237,0.14), rgba(37,99,235,0.14))' : 'rgba(124,58,237,0.06)'};
    color: #7c3aed;
  }

  .icon { display: inline-flex; flex-shrink: 0; }
  .label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .chev { color: #94a3b8; flex-shrink: 0; }
  .badge {
    flex-shrink: 0;
    background: #ef4444;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 999px;
    min-width: 16px;
    text-align: center;
  }
`

const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: ${p => p.theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 11px 4px;
`

const PremiumCard = styled.div`
  margin: 14px 4px 4px;
  padding: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
  box-shadow: 0 8px 20px rgba(124,58,237,0.25);

  .row { display: inline-flex; align-items: center; gap: 6px; }
  .title { font-size: 13px; font-weight: 800; letter-spacing: -0.2px; }
  .desc { font-size: 11px; opacity: 0.90; margin-bottom: 8px; line-height: 1.4; }

  button {
    width: 100%;
    padding: 7px 14px;
    background: rgba(255,255,255,0.95);
    color: #5b21b6;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { background: #ffffff; transform: translateY(-1px); }
  }
`

const LogoutBtn = styled.button`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 10px;
  text-align: left;
  &:hover { background: #fef2f2; }
`

const Spacer = styled.div`
  flex: 1;
`

export default function Navbar({ activePage, setActivePage, onMobileMessageClick, onMobileLoginClick, onNotificationClick, unreadNotificationCount }) {
  const { isLoggedIn, logout } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const go = (page, path) => {
    if (setActivePage) setActivePage(page)
    if (path) navigate(path)
  }

  const isActive = (page) => activePage === page

  const handleLogout = async () => {
    try {
      if (typeof logout === 'function') await logout()
    } catch (e) {
      console.error('Logout 실패:', e)
    } finally {
      navigate('/login')
    }
  }

  return (
    <Sidebar theme={theme}>
      <Brand theme={theme} onClick={() => go('home', '/home')}>
        <span className="badge">AR</span>
        <span className="text">
          <span className="title"><span className="accent">GREAT</span> ESCAPE</span>
          <span className="sub">AR · ESCAPE THE QUIZ</span>
        </span>
      </Brand>

      <NavLink theme={theme} $active={isActive('home')} onClick={() => go('home', '/home')}>
        <Home size={17} className="icon" />
        <span className="label">홈</span>
        <ChevronRight size={14} className="chev" />
      </NavLink>

      <SectionLabel theme={theme}>콘텐츠</SectionLabel>
      <NavLink theme={theme} $active={isActive('home')} onClick={() => go('home', '/home')}>
        <Box size={17} className="icon" />
        <span className="label">문제 세트</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('create')} onClick={() => go('create', '/create')}>
        <Plus size={17} className="icon" />
        <span className="label">만들기</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('myset')} onClick={() => go('myset', '/myset')}>
        <Bookmark size={17} className="icon" />
        <span className="label">보관함</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('objectupload')} onClick={() => go('objectupload', '/objectupload')}>
        <Package size={17} className="icon" />
        <span className="label">오브젝트 관리</span>
      </NavLink>

      <SectionLabel theme={theme}>커뮤니티</SectionLabel>
      <NavLink theme={theme} $active={isActive('community')} onClick={() => go('community', '/community')}>
        <Users size={17} className="icon" />
        <span className="label">커뮤니티</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('history')} onClick={() => go('history', '/history')}>
        <Clock size={17} className="icon" />
        <span className="label">히스토리</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('notice')} onClick={() => go('notice', '/notice')}>
        <Bell size={17} className="icon" />
        <span className="label">공지사항</span>
      </NavLink>

      <SectionLabel theme={theme}>설정</SectionLabel>
      <NavLink theme={theme} $active={isActive('settings')} onClick={() => go('settings', '/settings')}>
        <Settings size={17} className="icon" />
        <span className="label">설정</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('help')} onClick={() => go('help', '/help')}>
        <HelpCircle size={17} className="icon" />
        <span className="label">도움말 및 지원</span>
      </NavLink>
      <NavLink theme={theme} $active={isActive('bugreport')} onClick={() => go('bugreport', '/bugreport')}>
        <Bug size={17} className="icon" />
        <span className="label">버그 제보</span>
      </NavLink>

      <Spacer />

      <PremiumCard>
        <div className="row"><Crown size={18} /></div>
        <div className="title">프리미엄 플랜</div>
        <div className="desc">더 많은 기능을<br />경험해보세요!</div>
        <button onClick={() => alert('프리미엄 플랜 준비 중')}>업그레이드</button>
      </PremiumCard>

      {isLoggedIn && (
        <LogoutBtn onClick={handleLogout}>
          <LogOut size={15} /> 로그아웃
        </LogoutBtn>
      )}
    </Sidebar>
  )
}
